// BarraCompraMobile.js — Barra fixa no rodapé do celular com preço + "Comprar".
//
// No celular, o cartão de preço fica no topo e some assim que a pessoa começa a
// ler a sinopse. Daí em diante — metadados, autores, compre junto, opiniões,
// relacionados — não há mais nenhum caminho para a compra sem rolar de volta.
//
// A barra NÃO duplica a lógica de compra. Ela leva de volta ao cartão de preço
// e o destaca. Duplicar a escolha físico/e-book, quantidade e frete criaria uma
// segunda fonte de verdade para o carrinho, exatamente o tipo de divergência
// que já causou pedido com preço errado antes.
import React, { useEffect, useState } from "react";
import { m, AnimatePresence, useReducedMotion } from "motion/react";

const fmt = (v) => parseFloat(v).toFixed(2).replace(".", ",");

// `semPreco`: o livro só é vendido em loja parceira. A barra continua sendo o
// caminho de volta ao cartão, mas não anuncia valor — quem cobra é a outra loja,
// e o preço cadastrado aqui envelhece sem ninguém perceber.
// `gratis`: a versão digital foi liberada pela editora. A barra deixa de
// anunciar preço — anunciar "a partir de R$ 30,00" num livro que a pessoa pode
// baixar de graça é o pior anúncio possível, porque afasta exatamente quem
// aceitaria a oferta.
const BarraCompraMobile = ({ preco, precoOriginal = null, rotulo = "A partir de", alvoId = "cartao-de-preco", semPreco = false, gratis = false }) => {
  const [visivel, setVisivel] = useState(false);
  const reduzirMovimento = useReducedMotion();

  useEffect(() => {
    const alvo = document.getElementById(alvoId);
    if (!alvo) return;

    // A barra só aparece quando o cartão de preço saiu da tela: enquanto ele
    // estiver visível, ela seria uma segunda cópia do mesmo botão.
    const observador = new IntersectionObserver(
      ([entrada]) => setVisivel(!entrada.isIntersecting),
      { threshold: 0 }
    );
    observador.observe(alvo);
    return () => observador.disconnect();
  }, [alvoId]);

  const irParaCompra = () => {
    const alvo = document.getElementById(alvoId);
    if (!alvo) return;
    alvo.scrollIntoView({ behavior: reduzirMovimento ? "auto" : "smooth", block: "center" });
    // Pisca a borda do cartão para a pessoa achar de onde ela veio
    alvo.classList.add("realce-alvo");
    setTimeout(() => alvo.classList.remove("realce-alvo"), 1600);
  };

  if (!preco && !semPreco && !gratis) return null;

  return (
    <AnimatePresence>
      {visivel && (
        <m.div
          className="lg:hidden fixed bottom-0 left-0 right-0 z-[9998]
                     bg-insigne-navy-soft/95 backdrop-blur border-t border-line
                     px-4 py-3 flex items-center gap-3
                     shadow-[0_-8px_24px_rgba(0,0,0,0.3)]"
          style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
          initial={reduzirMovimento ? { opacity: 0 } : { y: 70 }}
          animate={reduzirMovimento ? { opacity: 1 } : { y: 0 }}
          exit={reduzirMovimento ? { opacity: 0 } : { y: 70 }}
          transition={{ duration: reduzirMovimento ? 0 : 0.25, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="min-w-0 flex-1">
            <p className="text-[11px] text-white/50 leading-none">{gratis ? "Versão digital" : rotulo}</p>
            {gratis ? (
              <p className="text-insigne-promo leading-tight mt-0.5 text-xl font-extrabold">
                Grátis
              </p>
            ) : semPreco ? (
              <p className="text-white leading-tight mt-0.5 text-sm font-semibold">
                Vendido em loja parceira
              </p>
            ) : (
              <p className="text-white leading-tight mt-0.5">
                <span className="text-xs align-top">R$</span>{" "}
                <span className="text-xl font-bold">{fmt(preco)}</span>
                {/* A barra so aparece depois que o cartao de preco sai da tela.
                    Sem o riscado aqui, o desconto some justamente no trecho da
                    pagina em que a pessoa esta decidindo. */}
                {precoOriginal > preco && (
                  <span className="ml-2 text-xs text-white/40 line-through">R$ {fmt(precoOriginal)}</span>
                )}
              </p>
            )}
          </div>
          {/* `rounded-full` está reservado ao círculo de verdade (avatar,
              bolinha, contador). Num botão retangular ele virava pílula, que é
              a forma que o dono tirou do site inteiro — e logo no botão que
              acompanha a pessoa por toda a rolagem do celular. */}
          <button
            onClick={irParaCompra}
            className="foco-laranja shrink-0 rounded-controle bg-insigne-orange text-paper font-semibold text-sm px-7 py-2.5
                       hover:bg-insigne-orange-dark transition-colors"
          >
            {gratis ? "Ler" : semPreco ? "Ver" : "Comprar"}
          </button>
        </m.div>
      )}
    </AnimatePresence>
  );
};

export default BarraCompraMobile;
